import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeadCapture() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting || done) return;
    if (!name.trim() || !email.trim()) {
      toast.error("Please add both your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: name.trim(),
        email: email.trim(),
      });
      setDone(true);
      toast.success("You're in. Watch your inbox for the next drop.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        setDone(true);
        toast.success(detail || "Already on the list — see you at the next drop.");
      } else {
        toast.error(
          typeof detail === "string"
            ? detail
            : "Something went sideways. Please try again."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="circle"
      className="relative bg-[#050505] border-t border-[#262626]"
      data-testid="lead-capture-section"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-36 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <p className="font-mono-alt text-[11px] tracking-[0.3em] uppercase text-[#c48a64]">
            / 004 — Inner Circle
          </p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl lg:text-[4.6rem] leading-[0.98] font-semibold text-[#f5f5f5]"
            data-testid="lead-headline"
          >
            Join the Collector <br />
            <span className="italic font-medium">& Maker</span>{" "}Inner Circle.
          </motion.h2>
          <p className="mt-8 max-w-md text-[#a3a3a3] text-base md:text-lg leading-relaxed">
            Early access to graded card drops, first dibs on new cotton
            colorways, and subscriber-only bundle pricing. No spam. Rare drops.
          </p>
        </div>

        <div className="md:col-span-7">
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={onSubmit}
            className="w-full"
            data-testid="lead-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-12">
              <label className="block">
                <span className="font-mono-alt text-[10px] tracking-[0.28em] uppercase text-[#6b6b6b]">
                  01 — Name
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your first name"
                  required
                  disabled={submitting || done}
                  className="input-line mt-3"
                  data-testid="lead-input-name"
                  autoComplete="given-name"
                />
              </label>
              <label className="block">
                <span className="font-mono-alt text-[10px] tracking-[0.28em] uppercase text-[#6b6b6b]">
                  02 — Email
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourinbox.com"
                  required
                  disabled={submitting || done}
                  className="input-line mt-3"
                  data-testid="lead-input-email"
                  autoComplete="email"
                />
              </label>
            </div>

            <div className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <p className="font-mono-alt text-[10px] tracking-[0.24em] uppercase text-[#6b6b6b] max-w-xs">
                By joining, you agree to receive drop notifications. Unsubscribe anytime.
              </p>
              <button
                type="submit"
                disabled={submitting || done}
                data-testid="lead-submit"
                className="btn-copper group inline-flex items-center justify-between gap-6 px-7 py-4 text-[13px] font-mono-alt tracking-[0.18em] uppercase min-w-[320px] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {done
                  ? "You're On The List"
                  : submitting
                  ? "Sending…"
                  : "Unlock Exclusive Drops & Deals"}
                {submitting ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <ArrowUpRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
